/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';

import Close from '@hyphengroup/cms-components/components/icons/Close';
import { RichText } from '@hyphengroup/cms-components/components/RichText';
import { usePinnedBottomHeight } from '@hyphengroup/cms-components/hooks/usePinnedBottomHeight';
import classNames from 'classnames';
import React, { useState, useEffect, useRef } from 'react';
import {
  ComponentSimpleStickyFooter,
  StickyFooterSimpleColorSchema,
} from '$/(cms)/_modules/cmsService/types';
import Button from '$/components/Button';
import { getTabTarget } from '$/components/cms/helpers/tabTarget';
import { pushDataLayer } from '$/components/cms/pushDataLayer';
import { FeLink } from '$/components/FeLink';
import { sessionAttributionTracker } from '$/modules/sessionAttributionTracking/sessionAttributionTracker';

interface ColorClasses {
  block_fill: string;
  title: string;
  button: string;
}

const getColorSchemeClasses = (
  colorScheme?: StickyFooterSimpleColorSchema,
): ColorClasses => {
  switch (colorScheme) {
    case 'GREEN':
      return {
        block_fill: 'bg-highlights/green-light',
        title: 'text-highlights/green-dark',
        button:
          '!border-campaign/green-3 !text-campaign/green-3 hover:bg-campaign/green-3 hover:!text-white',
      };
    case 'BLUE':
      return {
        block_fill: 'bg-highlights/blue-light',
        title: 'text-highlights/blue-dark',
        button:
          '!border-campaign/blue-3 !text-campaign/blue-3 hover:bg-campaign/blue-3 hover:!text-white',
      };
    case 'YELLOW':
      return {
        block_fill: 'bg-highlights/yellow-light',
        title: 'text-highlights/yellow-dark',
        button:
          '!border-campaign/yellow-3 !text-campaign/yellow-3 hover:bg-campaign/yellow-3 hover:!text-white',
      };
    case 'RED':
      return {
        block_fill: 'bg-highlights/red-light',
        title: 'text-highlights/red-dark',
        button:
          '!border-campaign/red-3 !text-campaign/red-3 hover:bg-campaign/red-3 hover:!text-white',
      };
    case 'ORANGE':
      return {
        block_fill: 'bg-highlights/orange-light',
        title: 'text-highlights/orange-dark',
        button:
          '!border-campaign/orange-3 !text-campaign/orange-3 hover:bg-campaign/orange-3 hover:!text-white',
      };
    case 'WHITE':
    default:
      return {
        block_fill: 'bg-white',
        title: 'text-fill/primary-dark',
        button:
          'mhg-alpha-btn-outline-primary hover:bg-fill/primary-dark hover:!text-white',
      };
  }
};

export const SimpleStickyFooter: React.FC<ComponentSimpleStickyFooter> = ({
  title,
  content,
  button,
  color_shema,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  // What the bar covers while it is slid in, for the Sort & Filter button and
  // rail to stay clear of it.
  const [bar, setBar] = useState<HTMLDivElement | null>(null);
  usePinnedBottomHeight(bar, isVisible);

  const colorClasses = getColorSchemeClasses(color_shema);
  const buttonTarget = getTabTarget(button?.target);
  const redirected_url =
    button?.product?.call_to_action?.links?.[0]?.redirected_url;

  const closeSticky = () => {
    setIsClosed(true);
    setIsVisible(false);
    pushDataLayer({
      event: 'navigate',
      component_type: 'bottom-sticky',
      component_title: `${title}_Close`,
      index: '',
      action: 'close',
    });
  };

  // Determine if this is an apply click:
  // - If applyClick field is explicitly set, use that value
  // - For existing data (backwards compatibility): if product is filled, it's an apply click
  const isApplyClick =
    button?.applyClick !== undefined
      ? button.applyClick
      : Boolean(button?.product);

  const sendPushData = () => {
    if (isApplyClick && button) {
      sessionAttributionTracker.recordApplyClick();
      pushDataLayer({
        event: 'product_click_apply',
        component_type: 'bottom-sticky',
        component_title: `${title}_${button?.text}`,
        action: 'click',
        items: [
          {
            item_id: button?.product?.uid ?? '',
            item_name: button?.product?.product_name ?? '',
            item_brand: button?.product?.provider?.provider_name ?? '',
            index: '',
          },
        ],
      });
    } else {
      pushDataLayer({
        event: 'navigate_click',
        component_type: 'bottom-sticky',
        component_title: `${title}_${button?.text}`,
        action: 'click',
        index: '',
      });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (isClosed || isFooterVisible) return;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      setIsVisible(scrollTop > 10);
    };

    const observeFooter = () => {
      const footer = document.querySelector('footer');
      if (!footer) return;

      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          setIsFooterVisible(entry.isIntersecting);
          if (entry.isIntersecting) {
            setIsVisible(false);
          }
        },
        { threshold: 0.1 },
      );

      observerRef.current.observe(footer);
    };

    window.addEventListener('scroll', handleScroll);
    observeFooter();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observerRef.current?.disconnect();
    };
  }, [isClosed, isFooterVisible]);

  if (isClosed) return null;

  return (
    <div
      ref={setBar}
      className={classNames(
        'fixed bottom-0 left-0 w-full z-50 duration-300 ease-out',
        {
          'translate-y-0': isVisible,
          'translate-y-full': !isVisible,
        },
      )}
    >
      <div className="!p-0 overflow-hidden">
        <div
          className={classNames(
            'flex h-full px-spacing/gap/x py-spacing/margin/y rounded-none flex-col md:flex-row shadow transition-all duration-300 ease-out',
            colorClasses.block_fill,
          )}
        >
          <div className="bg-transparent w-full md:max-w-[990px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-spacing/gap/x lg:items-center lg:justify-evenly">
              <div className="flex flex-col gap-spacing/gap/x-xs">
                <h3
                  className={classNames(
                    'flex justify-between mhg-alpha-typography-title-1',
                    colorClasses.title,
                  )}
                >
                  <FeLink
                    href={button?.url}
                    onClick={sendPushData}
                    target={buttonTarget}
                    className="hover:underline"
                  >
                    {title}
                  </FeLink>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={closeSticky}
                    className="lg:hidden"
                    aria-label="Close sticky footer"
                  >
                    <div className="cursor-pointer">
                      <span>
                        <Close className="size-shapes/icon-size-md text-text/primary" />
                      </span>
                    </div>
                  </div>
                </h3>
                <RichText className="mhg-alpha-typography-body-2 text-text/secondary">
                  {content}
                </RichText>
              </div>
              <div className="flex items-center">
                {button?.text && (button?.url || redirected_url) && (
                  <FeLink
                    href={button?.url || redirected_url}
                    target={buttonTarget}
                    className="w-full"
                  >
                    <Button
                      styleType="primary"
                      variant="outline"
                      className={classNames(
                        'w-full lg:w-auto',
                        colorClasses.button,
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        sendPushData();
                      }}
                    >
                      {button?.text}
                    </Button>
                  </FeLink>
                )}
              </div>
            </div>
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={closeSticky}
            className="hidden lg:block cursor-pointer shrink-0 self-center"
            aria-label="Close sticky footer"
          >
            <Close className="size-shapes/icon-size-md text-text/primary" />
          </div>
        </div>
      </div>
    </div>
  );
};
